<template>
  <v-navigation-drawer
    :value="sidebarVisible"
    :clipped="$vuetify.breakpoint.mdAndDown"
    :mini-variant="sidebarMini"
    :mini-variant-width="115"
    :stateless="stateless"
    :width="280"
    class="sidebar__container"
    app
    :right="$vuetify.rtl"
    disable-route-watcher
    @input="$store.commit('common/setSidebarVisible', $event)">
    <div class="v-navigation-drawer__action">
      <v-btn color="primary" icon small @click="$store.commit('common/setSidebarMini', !sidebarMini)">
        <v-icon small>$sidebar-open</v-icon>
      </v-btn>
    </div>
    <div class="v-navigation-drawer__logo">
      <c-logo :mini="sidebarMini" :account="account" info />
    </div>
    <v-list>
      <account-logo v-if="account" :account="account">
        <template v-if="account.clinicIds" #subTitle>{{ $t('forms.clinicIdLabel') }}: {{ account.clinicIds[0] }}</template>
      </account-logo>
      <v-list-item-group v-for="(item, i) in sidebarMenuListFiltered" :key="i" class="v-navigation-drawer__menu-list" color="primary">
        <div v-if="!item.children">
          <v-list-item
            v-if="account && account.role && item.roles.includes(account.role)"
            :key="i"
            class="list-item"
            :to="{ name: item.route }"
            :exact="!item.noExact"
            :disabled="item.disabled">
            <v-list-item-icon>
              <v-icon size="18" :class="item.class">{{ item.icon }}</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title :data-cy="setDataCyForTests(item.title)">{{ $i18n.t(item.title) }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <v-divider v-else-if="item.roles.includes('DIVIDER') && i !== menuList.length-1" :key="i" />
        </div>
        <v-list-group
          v-else-if="item.children"
          :key="i"
          no-action
          :value="false">
          <template #activator>
            <v-list-item-icon>
              <v-icon size="18" :class="item.class">{{ item.icon }}</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title :data-cy="setDataCyForTests(item.title)">{{ $i18n.t(item.title) }}</v-list-item-title>
            </v-list-item-content>
          </template>

          <div v-if="item.children">
            <v-list-item
              v-for="(children, key) in item.children"
              :key="key"
              :to="{ name: children.route }"
              :exact="!children.noExact"
              :disabled="children.disabled">
              <v-list-item-icon>
                <v-icon size="18" :class="children.class">{{ children.icon }}</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title :data-cy="setDataCyForTests(children.title)">{{ $i18n.t(children.title) }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </div>
        </v-list-group>
      </v-list-item-group>
    </v-list>
    <!-- <div v-if="!isAdminsRole" class="v-navigation-drawer__mobile">
      <div class="v-navigation-drawer__mobile-content">
        <span class="v-navigation-drawer__mobile-title">{{ $t('constants.getMobileApp') }}</span>
        <div class="v-navigation-drawer__mobile-icons">
          <v-btn v-if="googlePlayLink" class="white" icon large @click="openLink('googlePlayLink')">
            <v-icon>$google-play</v-icon>
          </v-btn>
          <v-btn v-if="appStoreLink" class="white" icon large @click="openLink('appStoreLink')">
            <v-icon>$app-store</v-icon>
          </v-btn>
        </div>
      </div>
    </div> -->
    <div class="v-navigation-drawer__bottom">
      <span>{{ $t('sidebar.selectLanguage') }}</span>
      <language-select :language="wlLanguage" :w100="false" />
    </div>
    <div class="v-navigation-drawer__bottom v-navigation-drawer__bottom--red" data-cy="logout" @click="logout()">
      <span>{{ $t('buttons.logout') }}</span>
      <v-btn :loading="logoutLoading" small icon color="#E93E3E">
        <v-icon>$logout</v-icon>
      </v-btn>
    </div>
  </v-navigation-drawer>
</template>

<script>
import { mapState } from 'vuex';
import { logout } from '@/common/preload/auth';
import permissionCheckMixin from '@/common/mixins/permissionCheckMixin';
import externalServicesMixin from '@/common/mixins/externalServicesMixin';
import { MY_CARE_CLIX_INFO, MY_CARE_CLIX_PERMISSIONS } from '@/common/constants';

import CLogo from '@/components/ui/CLogo';
import AccountLogo from '@/components/shared/AccountLogo';
import LanguageSelect from '@/components/ui/CLanguageSelect';

export default {
  name: 'LSidebar',
  inheritAttrs: false,
  components: {
    AccountLogo,
    LanguageSelect,
    CLogo,
  },
  data() {
    return {
      // googlePlayLinkCommon: 'https://play.google.com/store/apps/details?id=com.careclix.patient.preproduction',
      // googlePlayLinkProvider: 'https://play.google.com/store/apps/details?id=com.careclix.doctor.preproduction&hl=en_US&gl=US',
      // appStoreLinkCommon: 'https://apps.apple.com/us/app/careclix/id1460083225',
      // appStoreLinkProvider: 'https://apps.apple.com/us/app/careclix-provider/id1460083142',
      previousRouteName: '',
      logoutLoading: false,
      sidebarMenuListFiltered: [],
    };
  },
  mixins: [permissionCheckMixin, externalServicesMixin],
  computed: {
    ...mapState('common', ['whiteLabel', 'sidebarVisible', 'sidebarMini', 'sidebarMenuList']),
    ...mapState('loggedAccount', ['account', 'accountClinic']),
    isAdminsRole() {
      const adminsRole = ['CLINIC_ADMIN', 'SUPER_ADMIN'];
      return adminsRole.includes(this.account.role);
    },
    // googlePlayLink() {
    //   return this.account.role === 'PROVIDER' ? this.googlePlayLinkProvider : this.googlePlayLinkCommon;
    // },
    // appStoreLink() {
    //   return this.account.role === 'PROVIDER' ? this.appStoreLinkProvider : this.appStoreLinkCommon;
    // },
    wlLanguage() {
      return this.whiteLabel ? this.whiteLabel.language : null;
    },
    stateless() {
      const settings = this.$route.params.appElementSettings || [];
      return settings.includes('hideSidebar');
    },
    myGuardianDocAllowance() {
      return this.checkSubscriptionForMyGuardianDocMain(false);
    },
    isMyGuardianDoc() {
      if (this.account.clinicIds) {
        return MY_CARE_CLIX_INFO.CLINIC_IDS.ALL.includes(...this.account.clinicIds);
      }
    },
    isServicesEnabled() {
      return this.accountClinic?.features?.some(feature => feature.name === MY_CARE_CLIX_PERMISSIONS.SERVICES);
    },
  },
  watch: {
    myGuardianDocAllowance() {
      this.setMenuList();
    },
  },
  mounted() {
    this.setMenuList();

    // if (this.whiteLabel) {
    //   this.appStoreLink = this.whiteLabel.appStoreLink;
    //   this.googlePlayLink = this.whiteLabel.googlePlayLink;
    // }
  },
  methods: {
    setDataCyForTests(title) {
      return `${title.replace('.', '-')}`;
    },
    logout() {
      console.log('logout method');
      this.logoutLoading = true;
      logout().finally(() => this.logoutLoading = false);
    },
    openLink(url) {
      window.open(this[url], '_blank');
    },
    setMenuList() {
      this.sidebarMenuListFiltered = [];

      this.sidebarMenuList.forEach(item => {
        if (item.title === 'sidebar.messages' && !this.checkFeatureAllowance('messaging')) return;

        let disabled = false;
        if (this.account?.role === 'PATIENT' && !this.myGuardianDocAllowance && item.additionalRules?.PATIENT?.requireMyGuardianDocSubscription) disabled = true;
        if ((this.isMyGuardianDoc ? !this.isServicesEnabled : true) && item.additionalRules?.SERVICES?.iSMyGuardianDoc) return;
        this.sidebarMenuListFiltered.push({ ...item, disabled });
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.sidebar-care-clix-version {
  margin-top: -20px;
}
.sidebar__container {
  height: 100vh;

    @include until('sm') {
    height: calc(var(--vh, 1vh) * 100) !important;
  }
}

.list-item {
  max-height: 56px;
}
</style>
